import { Button, Dialog, Divider, Flex, Popover, PopoverProps, Stack, Text } from '@eleveo/components-react';
import { ChartGanttIcon, CheckmarkIcon, CrossIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { parseAbsolute, toCalendarDateTime } from '@internationalized/date';
import clsx from 'clsx';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BriefScheduleOverview } from './BriefScheduleOverviewNext';
import { transformScheduleElementsOnly } from './transformData';
import { useAgentBriefScheduleOverview } from './useAgentBriefScheduleOverview';
import { useAuth } from '../../app/AuthorizationProvider';
import { SHIFT_SWAP_REQUEST_CONTROLLERS_TO_SUBTYPE_MAP } from '../../config/agentsRequests';
import {
  AgentInRequest,
  AgentInRequestWithQueues,
  AgentShift,
  DateRange,
  Queue,
  RequestSubtype,
  ScheduleElement,
  ShiftScheduleItem,
} from '../../graphql/components';
import { formatNameOfPerson } from '../../utils/helpers';
import { DateFormatter } from '../DateFormatter';
import { AvatarLabel } from '../LayoutNext/components/AvatarLabel';

import type { CalendarDateTime } from '@internationalized/date';

type Props = {
  className?: string;
  onSelect?: (agentId?: string, from?: CalendarDateTime) => void;
  isDisabled?: boolean;
  isSelected: boolean;
  isSupervisor?: boolean;
  agent: AgentInRequest | AgentInRequestWithQueues;
  dateRange: DateRange;
  scheduleItems?: ShiftScheduleItem[]; // for Sidebar usage, the items would not be preloaded, they need to be fetched only after user opens brief overview
  scheduleElements: ScheduleElement[];
  shiftSubtype: RequestSubtype.Requester | RequestSubtype.Recipient;
  shiftSwapRequestId?: string;
  testIdSlug?: string; // should contain most probably 'recipient' or 'requester' values
  queues?: Record<string, Queue>; // should contain all queues object to map shift queueId when needed to display shiftQueues information
  queueIds?: string[]; // currently not intended to use but defined to keep consist logic when shift related data are passed directly
};

export const AgentShiftTile: React.FC<Props> = ({
  agent,
  className,
  dateRange,
  isDisabled,
  isSelected,
  isSupervisor,
  onSelect,
  scheduleElements,
  scheduleItems: defaultScheduleItems,
  shiftSubtype,
  shiftSwapRequestId,
  testIdSlug = 'agent',
  queues,
  queueIds,
}) => {
  const { t } = useTranslation();
  const { timezone } = useAuth();
  const popoverTargetRef = useRef(null);

  const [briefScheduleOverview, setBriefScheduleOverview] = useState<AgentShift | null>(null);

  const [getData, { loading: isLoadingBriefScheduleOverview }] = useAgentBriefScheduleOverview({
    isSupervisor,
    onCompleted: setBriefScheduleOverview,
    variables: {
      userId: agent.id,
      controller: SHIFT_SWAP_REQUEST_CONTROLLERS_TO_SUBTYPE_MAP[shiftSubtype],
      shiftSwapRequestId: shiftSwapRequestId ?? '',
    },
  });

  const overviewScheduleItems = useMemo(
    () => transformScheduleElementsOnly(briefScheduleOverview?.scheduleItems ?? [], scheduleElements, t),
    [briefScheduleOverview, scheduleElements, t]
  );
  const hasOverviewScheduleItems = overviewScheduleItems.length > 0;

  const startDate = toCalendarDateTime(parseAbsolute(dateRange.from, timezone));

  // Loaded brief overview date has a priority over default passed data.
  const briefScheduleOverviewDate = briefScheduleOverview
    ? toCalendarDateTime(parseAbsolute(briefScheduleOverview.dateRange.from, timezone))
    : startDate;

  const agentName = formatNameOfPerson(agent.firstName, agent.lastName);

  const timeFormatterOptions = {
    timeStyle: 'short' as const,
    timeZone: timezone,
  };

  const handleOnSelect = () => onSelect?.(agent.id, startDate);
  const handleUnselect = () => onSelect?.();

  const handleOpenBriefOverview = () => {
    if (defaultScheduleItems && defaultScheduleItems.length > 0) {
      setBriefScheduleOverview({
        agent: agent as AgentShift['agent'],
        dateRange,
        scheduleItems: defaultScheduleItems,
        queueIds: queueIds ?? [],
      });
    } else {
      getData();
    }
  };

  const toggleAction = isSelected ? handleUnselect : handleOnSelect;
  const isEnabled = !!onSelect;

  return (
    <div
      className={clsx('flex border rounded-sm border-grey-300 transition-colors', className, {
        'bg-blue-300 border-blue-300': isSelected,
        'bg-white': !isSelected && !isDisabled,
        'bg-grey-200 text-grey-600': isDisabled,
        'cursor-pointer hover:border-blue-700 select-none': isEnabled,
      })}
      data-test-id={`${testIdSlug}-shift-tile:container`}
      {...(isEnabled && {
        onClick: toggleAction, // makes element clickable but not focusable by keyboard (the action is still accessible by dedicated button)
      })}
    >
      <Flex alignItems="center" className="px-20 py-10" column testId={`${testIdSlug}-shift-tile:date-of-shift`}>
        <Text weight="bold" align="center">
          <DateFormatter options={{ year: 'numeric', timeZone: timezone }}>{startDate.toDate(timezone)}</DateFormatter>
        </Text>

        <Text weight="bold" align="center" className="text-center">
          <DateFormatter options={{ day: 'numeric', month: 'short', timeZone: timezone }}>
            {startDate.toDate(timezone)}
          </DateFormatter>
        </Text>

        <span className="font-normal text-sm">
          (
          <DateFormatter options={{ weekday: 'short', timeZone: timezone }}>{startDate.toDate(timezone)}</DateFormatter>
          )
        </span>
      </Flex>

      <Divider direction="vertical" />

      <Flex className="flex-1 p-10 pr-0 w-0" column>
        <AvatarLabel
          className="mb-auto pr-10"
          isColorized
          isDisabled={isDisabled}
          name={agentName}
          size={25}
          testId={`${testIdSlug}-shift-tile:agent-info`}
        />

        <Stack className="items-center text-sm" spacing={15} testId={`${testIdSlug}-shift-tile:time-range-of-shift`}>
          <div>
            {t('time.start')}:{' '}
            <DateFormatter className="font-bold" options={timeFormatterOptions}>
              {startDate.toDate(timezone)}
            </DateFormatter>
          </div>

          <div>
            {t('time.end')}:{' '}
            <DateFormatter className="font-bold" options={timeFormatterOptions}>
              {toCalendarDateTime(parseAbsolute(dateRange.to, timezone)).toDate(timezone)}
            </DateFormatter>
          </div>
        </Stack>
      </Flex>

      <Flex className="p-10 pl-0" column>
        <Button
          aria-label={t('schedule.request.shiftDetail')}
          className="mb-auto"
          isSelected={hasOverviewScheduleItems}
          isSmall
          /**
           * TODO: this needs to be confirmed with PO whether the preview of shift should be enabled
           * even thought the state of agent shift tile is disabled.
           *
           * Should be resolved when adding shift overview functionality for sidebar.
           */
          // isDisabled={isDisabled}
          onClick={handleOpenBriefOverview}
          ref={popoverTargetRef}
          isPending={isLoadingBriefScheduleOverview}
          testId={`${testIdSlug}-shift-tile:shift-detail-view`}
          variant="outline"
        >
          <ChartGanttIcon />
        </Button>

        {isEnabled &&
          (isSelected ? (
            <Button
              aria-label={t('schedule.request.unselectShiftForSwap')}
              color="warning"
              isSmall
              onClick={handleUnselect}
              testId={`${testIdSlug}-shift-tile:unselect-shift`}
              variant="outline"
            >
              <CrossIcon />
            </Button>
          ) : (
            <Button
              aria-label={t('schedule.request.selectShiftForSwap')}
              color="success"
              isSmall
              onClick={handleOnSelect}
              testId={`${testIdSlug}-shift-tile:select-shift`}
              variant="outline"
            >
              <CheckmarkIcon />
            </Button>
          ))}

        <Popover
          triggerRef={popoverTargetRef}
          state={
            {
              isOpen: hasOverviewScheduleItems,
              close: () => setBriefScheduleOverview(null),
            } as PopoverProps['state']
          }
        >
          <Dialog>
            <BriefScheduleOverview
              convertToZonedDate={false}
              date={briefScheduleOverviewDate.toDate(timezone).getTime()}
              elements={overviewScheduleItems}
              testIdSlug={`${testIdSlug}-shift-tile`}
              t={t}
              timezone={timezone}
            />

            {/* Shift Queues */}
            {queues && (
              <>
                <Divider className="mt-15 mb-10" />

                <Text weight="bold" align="center" className="text-center bg-blue-100 rounded-sm mb-10 p-10">
                  {t('queue.shiftQueues')}
                </Text>

                <Stack direction="vertical">
                  {briefScheduleOverview?.queueIds
                    .filter((queueId) => !!queues[queueId])
                    .map((queueId) => (
                      <Text
                        align="center"
                        className="break-words overflow-auto"
                        key={queueId}
                        testId={`${testIdSlug}-shift:queue:${queueId}`}
                      >
                        {queues[queueId]?.name}
                      </Text>
                    ))}
                </Stack>
              </>
            )}
          </Dialog>
        </Popover>
      </Flex>
    </div>
  );
};
